<template>
  <div class="restaurant-menu-page">
    <!-- Load menu on mount -->
    <VBusHelper
      :emitOnMounted="$constants.EVENTS.MENU.LOAD"
      :eventPayload="{ restaurantId: $route.params.id }"
    />

    <!-- Loading State -->
    <div v-if="$stores.restaurants.state.menu.loading" class="loading">
      <div class="spinner"></div>
      <p>Chargement du menu...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="$stores.restaurants.state.menu.error" class="error">
      <p>{{ $stores.restaurants.state.menu.error }}</p>
      <button @click="retry" class="retry-button">Réessayer</button>
    </div>

    <!-- Menu Content -->
    <div v-else class="menu-content">
      <!-- Header -->
      <div class="menu-header">
        <button @click="goBack" class="back-button">← Retour</button>
        <h1>Menu</h1>
      </div>

      <!-- Popular Items -->
      <div v-if="popularItems.length > 0" class="section">
        <h2 class="section-title">Populaires</h2>
        <div class="items-grid">
          <div
            v-for="item in popularItems"
            :key="item.id"
            class="menu-item-card"
            @click="showItemDetail(item)"
          >
            <div class="item-image">
              <ImgComponent :src="item.image" :alt="item.name" />
              <div v-if="item.popular" class="popular-badge">Populaire</div>
            </div>
            <div class="item-info">
              <h3 class="item-name">{{ item.name }}</h3>
              <p class="item-description">{{ item.description }}</p>
              <div class="item-meta">
                <span class="item-price"
                  >{{ item.price }} {{ item.currency }}</span
                >
                <span class="item-time">{{ item.preparationTime }}</span>
              </div>
              <div class="item-actions">
                <button
                  class="add-button"
                  @click.stop="addToCart(item)"
                  :disabled="!item.available"
                >
                  {{ item.available ? 'Ajouter' : 'Indisponible' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu Categories -->
      <div
        v-for="category in menuCategories"
        :key="category.id"
        class="section"
      >
        <h2 class="section-title">{{ category.name }}</h2>
        <p v-if="category.description" class="section-description">
          {{ category.description }}
        </p>

        <div class="items-grid">
          <div
            v-for="item in category.items"
            :key="item.id"
            class="menu-item-card"
            @click="showItemDetail(item)"
          >
            <div class="item-image">
              <ImgComponent :src="item.image" :alt="item.name" />
              <div v-if="item.popular" class="popular-badge">Populaire</div>
              <div v-if="item.vegetarian" class="vegetarian-badge">V</div>
              <div v-if="item.spicy > 0" class="spicy-badge">
                <span v-for="i in item.spicy" :key="i">🌶</span>
              </div>
            </div>
            <div class="item-info">
              <h3 class="item-name">{{ item.name }}</h3>
              <p class="item-description">{{ item.description }}</p>
              <div class="item-meta">
                <span class="item-price"
                  >{{ item.price }} {{ item.currency }}</span
                >
                <span class="item-time">{{ item.preparationTime }}</span>
              </div>
              <div class="item-actions">
                <button
                  class="add-button"
                  @click.stop="addToCart(item)"
                  :disabled="!item.available"
                >
                  {{ item.available ? 'Ajouter' : 'Indisponible' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="menuCategories.length === 0 && popularItems.length === 0"
        class="empty"
      >
        <p>Menu non disponible</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const $route = useRoute();

const menuCategories = computed(() => {
  return window.$stores?.restaurants?.state?.menu?.categories || [];
});

const popularItems = computed(() => {
  return window.$stores?.restaurants?.state?.menu?.popularItems || [];
});

const retry = () => {
  window.$bus?.emit(window.$constants?.EVENTS?.MENU?.LOAD, {
    restaurantId: $route.params.id
  });
};

const goBack = () => {
  window.$router?.back();
};

const showItemDetail = (item) => {
  // TODO: Show item detail modal
  console.log('Show item detail:', item);
};

const addToCart = (item) => {
  window.$bus?.emit(window.$constants?.EVENTS?.CART?.ITEMS?.ADD, {
    restaurantId: $route.params.id,
    dishId: item.id,
    quantity: 1
  });
};
</script>

<style scoped>
.restaurant-menu-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.loading,
.error {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #f59e0b;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error {
  color: #dc2626;
}

.retry-button {
  margin-top: 16px;
  padding: 10px 20px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.menu-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.back-button {
  background: transparent;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
}

.menu-header h1 {
  font-size: 2em;
  font-weight: bold;
}

.section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 1.5em;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
}

.section-description {
  color: #666;
  margin-bottom: 20px;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.menu-item-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}

.menu-item-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.item-image {
  position: relative;
  height: 180px;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.popular-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #f59e0b;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8em;
  font-weight: 600;
}

.vegetarian-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #10b981;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.spicy-badge {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 0.8em;
}

.item-info {
  padding: 16px;
}

.item-name {
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
}

.item-description {
  color: #666;
  font-size: 0.9em;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 0.9em;
}

.item-price {
  font-weight: 600;
  color: #f59e0b;
  font-size: 1.1em;
}

.item-time {
  color: #666;
}

.item-actions {
  display: flex;
  gap: 8px;
}

.add-button {
  flex: 1;
  padding: 10px 16px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.add-button:hover:not(:disabled) {
  background: #e0890b;
}

.add-button:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

.empty {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}
</style>
